import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule, FormArray, FormGroup, FormControl } from '@angular/forms';
import { SharedServiceService } from '../../../services/shared-service.service';
import { ModelInputsComputationService, ModelInputsViewData } from '../../../services/model-inputs-computation.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface DropdownOptions {
  Unit: string[];
  Family: string[];
  Metal: string[];
}

export interface AdditiveRow {
  Additive: string;
  Dosage: number;
  Unit: string;
  Family: string;
  Metal: string;
  'Typ. Metal %': number;
}

@Component({
  selector: 'app-bc-fob-model-inputs',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './bc-fob-model-inputs.component.html',
  styleUrl: './bc-fob-model-inputs.component.scss'
})

/**
 * Model Inputs modal for one blend.
 *
 * Core responsibilities:
 * - Build backend input from current selection (VM/BO/DI).
 * - Merge backend defaults with previously saved overrides.
 * - Persist edited model-input rows into SharedService legacy stores.
 *
 * Source of truth priority:
 * 1) Current selections on main screen.
 * 2) Saved overrides that still match current selection.
 * 3) Fresh backend defaults for new rows.
 */
export class BcFobModelInputsComponent implements OnChanges, OnInit, OnDestroy {
  // Inputs from parent component
  @Input() blendNumber: number = 1;
  @Input() packageData: any[] = []; // Data for Additives
  @Input() vmData: any[] = []; // Data for Viscosity Modifier
  @Input() bodata: any[] = []; // Data for Base Oils

  @Output() close = new EventEmitter<void>();
  @Output() predictionComplete = new EventEmitter<any>();

  // Headers for the tables
  vmHeaders: string[] = ['VM', 'SSI30', 'SSI90', 'KV100', 'KV40', 'NOACK']
  ppdHeaders: string[] = ['PPD', 'KV100', 'KV40'];
  baseOilHeaders: string[] = ['Base Oil', 'Batch', 'Group', 'Saturate', 'Sulfur',
    'KV100', 'KV40', 'VI', 'NOACK', 'CCS-10', 'CCS-15', 'CCS-20', 'CCS-25', 'CCS-30', 'CCS-35'];
  additiveHeaders: string[] = ['Additive', 'Dosage', 'Unit', 'Family', 'Metal', 'Typ. Metal %'];

  // Data for the tables
  additives: any = [];
  unitOptions: any[] = [];
  familyOptions: any[] = [];
  metalOptions: any[] = [];

  vmInfo: any[] = [];
  baseOilInfo: any[] = [];

  // Reactive Forms
  vmForm = new FormArray<FormGroup>([]);
  baseOilForm = new FormArray<FormGroup>([]);

  // New properties for state handling
  loading: boolean = false;
  submitButtonColor: string = '#0066b2';
  modelInputsData: any = null;
  modelInputLength: any;

  // State of persistence
  saveSuccess: boolean = false;
  saveMessage: string = '';

  private destroy$ = new Subject<void>();
  private vmFormSubscription: any;
  private baseOilFormSubscription: any;
  private vmSelectionLoaded: boolean = false;

  constructor(private sharedService: SharedServiceService,
    private miService: ModelInputsComputationService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['packageData'] && changes['packageData'].currentValue) {
      this.processPackageData(changes['packageData'].currentValue);
      // Also refresh Model Inputs when the parent changes the input directly
      this.loadModelInputsData();
    }
    if (changes['vmData'] && changes['vmData'].currentValue) {
      // `vmData` for this modal represents the selected VMs (rows with selectedValue/treat1/treat2).
      // Mark it as ready to prevent prune/payload from leaving mod_inp_vm empty on open.
      this.vmSelectionLoaded = true;
      this.vmData = changes['vmData'].currentValue;
      this.processVMData(this.vmData);
      this.loadModelInputsData();
    }
  }

  ngOnInit(): void {
    // Subscribe to packages from SharedService (only once per instance)
    this.sharedService.packagesData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data && data.length > 0) {
          this.packageData = data;
          this.processPackageData(data);
          // Update Model Inputs every time the packages change
          this.loadModelInputsData();
        }
      });

    // Subscribe to selected VMs
    this.sharedService.vmSelectedData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.vmSelectionLoaded = true;
        this.vmData = data;
        this.processVMData(this.vmData);
        // Recalculate Model Inputs when the selected VMs change
        this.loadModelInputsData();
      });

    // Subscribe to selected Base Oils
    this.sharedService.baseOilSelectedData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.bodata = data;
        this.processBaseOilData(data);
        // Every change in Base Oils must be reflected in the Model Inputs
        this.loadModelInputsData();
      });
  }

  // #region Load and merge pipeline

  ngOnDestroy(): void {
    // Unsubscribe from form subscriptions
    if (this.vmFormSubscription) {
      this.vmFormSubscription.unsubscribe();
    }
    if (this.baseOilFormSubscription) {
      this.baseOilFormSubscription.unsubscribe();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  // Delegates derivation (backend + merge preserving edits + persist) to the shared service.
  loadModelInputsData(): void {
    if (!this.vmSelectionLoaded) {
      return;
    }

    this.miService.deriveAndPersist(this.blendNumber, 'modal-load')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (view) => {
          if (view) {
            this.applyDerivedView(view);
          }
        },
        error: (error) => {
          console.error('❌ [ModelInputs] Error:', error);
        }
      });
  }

  private applyDerivedView(view: ModelInputsViewData): void {
    this.vmInfo = view.vmInfo;
    this.baseOilInfo = view.baseOilInfo;
    this.additives = view.additives;
    if (view.unitOptions) {
      this.unitOptions = view.unitOptions;
    }
    if (view.familyOptions) {
      this.familyOptions = view.familyOptions;
    }
    if (view.metalOptions) {
      this.metalOptions = view.metalOptions;
    }
    this.syncVmForm();
    this.syncBaseOilForm();
  }

  // #endregion

  // Save method only stores overrides. Prediction execution remains in parent component.
  onSubmit(): void {
    // Save to the legacy BehaviorSubjects (single source of truth)
    this.miService.persistViewData(
      this.blendNumber,
      { vmInfo: this.vmInfo, baseOilInfo: this.baseOilInfo, additives: this.additives },
      { persistVm: true },
      this.vmData
    );

    // Show success feedback
    this.saveSuccess = true;
    this.saveMessage = 'Model Inputs saved successfully';
    this.submitButtonColor = '#28a745'; // Green to indicate success

    // Clear the message after 3 seconds
    setTimeout(() => {
      this.saveSuccess = false;
      this.saveMessage = '';
      this.submitButtonColor = '#0066b2'; // Restaurar color original
    }, 3000);
  }


  processPackageData(data: any[]): void {
    this.additives = this.miService.buildAdditives(data, this.blendNumber);
  }

  processVMData(data: any[]): void {
    this.vmInfo = this.miService.buildVmInfo(data);
    this.syncVmForm();
  }

  // Synchronize vmInfo with vmForm (Reactive Forms)
  private syncVmForm(): void {
    // If the FormArray already has the same number of elements, update values
    if (this.vmForm.length === this.vmInfo.length) {
      this.vmInfo.forEach((row, index) => {
        const formGroup = this.vmForm.at(index) as FormGroup;
        if (formGroup && row.name) {
          formGroup.patchValue({
            name: this.toNaNIfEmpty(row.name),
            SSI30: this.formatDecimal(row.SSI30),
            SSI90: this.formatDecimal(row.SSI90),
            KV100: this.formatDecimal(row.KV100),
            KV40: this.formatDecimal(row.KV40),
            NOACK: this.formatDecimal(row.NOACK)
          }, { emitEvent: false }); // emitEvent: false to avoid loops
        }
      });
    } else {
      // If the number of elements changed, recreate the entire FormArray
      this.vmForm.clear();
      this.vmInfo.forEach(row => {
        if (row.name) {
          this.vmForm.push(new FormGroup({
            name: new FormControl(this.toNaNIfEmpty(row.name)),
            SSI30: new FormControl(this.formatDecimal(row.SSI30)),
            SSI90: new FormControl(this.formatDecimal(row.SSI90)),
            KV100: new FormControl(this.formatDecimal(row.KV100)),
            KV40: new FormControl(this.formatDecimal(row.KV40)),
            NOACK: new FormControl(this.formatDecimal(row.NOACK))
          }));
        }
      });
    }

    // Subscribe to form changes to update vmInfo
    // (only if the subscription does not already exist)
    if (!this.vmFormSubscription) {
      this.vmFormSubscription = this.vmForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(values => {
          values.forEach((val: any, i: number) => {
            if (this.vmInfo[i]) {
              Object.assign(this.vmInfo[i], val);
            }
          });
        });
    }
  }

  processBaseOilData(data: any[]) {
    this.baseOilInfo = this.miService.buildBaseOilInfo(data);
    this.syncBaseOilForm();
  }

  // Synchronize baseOilInfo with baseOilForm (Reactive Forms)
  private syncBaseOilForm(): void {
    // If the FormArray already has the same number of elements, update values
    if (this.baseOilForm.length === this.baseOilInfo.length) {
      this.baseOilInfo.forEach((row, index) => {
        const formGroup = this.baseOilForm.at(index) as FormGroup;
        if (formGroup && row.name) {
          formGroup.patchValue({
            name: this.toNaNIfEmpty(row.name),
            batch: this.toNaNIfEmpty(row.batch),
            group: this.toNaNIfEmpty(row.group),
            saturate: this.formatDecimal(row.saturate),
            sulfur: this.formatDecimal(row.sulfur, 4),
            KV100: this.formatDecimal(row.KV100),
            KV40: this.formatDecimal(row.KV40),
            VI: this.formatDecimal(row.VI, 0),
            NOACK: this.formatDecimal(row.NOACK, 2),
            CCS10: this.formatDecimal(row.CCS10, 0),
            CCS15: this.formatDecimal(row.CCS15, 0),
            CCS20: this.formatDecimal(row.CCS20, 0),
            CCS25: this.formatDecimal(row.CCS25, 0),
            CCS30: this.formatDecimal(row.CCS30, 0),
            CCS35: this.formatDecimal(row.CCS35, 0)
          }, { emitEvent: false });

          // Disable CCS fields that do not require user input
          formGroup.get('CCS10')?.disable({ emitEvent: false });
          formGroup.get('CCS15')?.disable({ emitEvent: false });
          formGroup.get('CCS35')?.disable({ emitEvent: false });
        }
      });
    } else {
      // If the number of elements changed, recreate the entire FormArray
      this.baseOilForm.clear();
      this.baseOilInfo.forEach(row => {
        if (row.name) {
          const formGroup = new FormGroup({
            name: new FormControl(this.toNaNIfEmpty(row.name)),
            batch: new FormControl(this.toNaNIfEmpty(row.batch)),
            group: new FormControl(this.toNaNIfEmpty(row.group)),
            saturate: new FormControl(this.formatDecimal(row.saturate)),
            sulfur: new FormControl(this.formatDecimal(row.sulfur, 4)),
            KV100: new FormControl(this.formatDecimal(row.KV100)),
            KV40: new FormControl(this.formatDecimal(row.KV40)),
            VI: new FormControl(this.formatDecimal(row.VI, 0)),
            NOACK: new FormControl(this.formatDecimal(row.NOACK, 2)),
            CCS10: new FormControl({ value: this.formatDecimal(row.CCS10, 0), disabled: true }),
            CCS15: new FormControl({ value: this.formatDecimal(row.CCS15, 0), disabled: true }),
            CCS20: new FormControl(this.formatDecimal(row.CCS20, 0)),
            CCS25: new FormControl(this.formatDecimal(row.CCS25, 0)),
            CCS30: new FormControl(this.formatDecimal(row.CCS30, 0)),
            CCS35: new FormControl({ value: this.formatDecimal(row.CCS35, 0), disabled: true })
          });
          this.baseOilForm.push(formGroup);
        }
      });
    }

    // Subscribe to form changes to update baseOilInfo
    if (!this.baseOilFormSubscription) {
      this.baseOilFormSubscription = this.baseOilForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(values => {
          values.forEach((val: any, i: number) => {
            if (this.baseOilInfo[i]) {
              Object.assign(this.baseOilInfo[i], val);
            }
          });
        });
    }
  }

  // Helper method: validate whether a VM has a treat value for the current blend
  hasValidTreat(vmName: string): boolean {
    const treatField = this.blendNumber === 1 ? 'treat1' : 'treat2';
    const vmItem = this.vmData.find(vm => vm.selectedValue === vmName);
    const treatValue = vmItem ? vmItem[treatField] : null;
    return treatValue && treatValue !== '0' && treatValue !== null && treatValue !== undefined;
  }

  /**
  * Helper method: validate whether a Base Oil has a valid ratio for the current blend
  * Similar to hasValidTreat() for VM
   */
  hasValidRatio(boName: string, boBatch: string): boolean {
    if (!boName || !this.bodata) return false;

    const ratioField = this.blendNumber === 1 ? 'ratio1' : 'ratio2';

    const boItem = this.bodata.find((b: any) => {
      const name = b.oil ?? b.name ?? '';
      const batch = b.batch ?? '';
      return name === boName && batch === boBatch;
    });

    if (!boItem) return false;

    const ratioValue = boItem[ratioField];
    return ratioValue && ratioValue !== '0' && ratioValue !== 0 && ratioValue !== null;
  }

  // Replace your existing updateValue method with this adapted version
  updateValue(section: string, index: number, field: string, value: any): void {
    switch (section) {
      case 'vm':
        if (this.vmInfo[index]) {
          // Map fields according to your vmInfo structure
          switch (field) {
            case 'SSI30':
              this.vmInfo[index].SSI30 = this.parseNumericValue(value);
              break;
            case 'SSI90':
              this.vmInfo[index].SSI90 = this.parseNumericValue(value);
              break;
            case 'KV40':
              this.vmInfo[index].KV40 = this.parseNumericValue(value);
              break;
            case 'KV100':
              this.vmInfo[index].KV100 = this.parseNumericValue(value);
              break;
            case 'NOACK':
              this.vmInfo[index].NOACK = this.parseNumericValue(value);
              break;
          }
        }
        break;

      case 'baseOil':
        if (this.baseOilInfo[index]) {
          // Map fields according to your baseOilInfo structure
          switch (field) {
            case 'group':
              this.baseOilInfo[index].group = this.parseNumericValue(value);
              break;
            case 'saturate':
              this.baseOilInfo[index].saturate = this.parseNumericValue(value);
              break;
            case 'sulfur':
              this.baseOilInfo[index].sulfur = this.parseNumericValue(value);
              break;
            case 'KV100':
              this.baseOilInfo[index].KV100 = this.parseNumericValue(value);
              break;
            case 'KV40':
              this.baseOilInfo[index].KV40 = this.parseNumericValue(value);
              break;
            case 'VI':
              this.baseOilInfo[index].VI = this.parseNumericValue(value);
              break;
            case 'NOACK':
              this.baseOilInfo[index].NOACK = this.parseNumericValue(value);
              break;
            case 'CCS10':
              this.baseOilInfo[index].CCS10 = this.parseNumericValue(value);
              break;
            case 'CCS15':
              this.baseOilInfo[index].CCS15 = this.parseNumericValue(value);
              break;
            case 'CCS20':
              this.baseOilInfo[index].CCS20 = this.parseNumericValue(value);
              break;
            case 'CCS25':
              this.baseOilInfo[index].CCS25 = this.parseNumericValue(value);
              break;
            case 'CCS30':
              this.baseOilInfo[index].CCS30 = this.parseNumericValue(value);
              break;
            case 'CCS35':
              this.baseOilInfo[index].CCS35 = this.parseNumericValue(value);
              break;
          }
        }
        break;

      case 'additive':
        if (this.additives[index]) {
          // Map fields according to your additives structure
          switch (field) {
            case 'dosage':
              this.additives[index].dosage = this.parseNumericValue(value);
              break;
            case 'unit':
              this.additives[index].unit = value;
              break;
            case 'family':
              this.additives[index].family = value;
              break;
            case 'metal':
              this.additives[index].metal = value;
              break;
            case 'typicalMetal':
              this.additives[index].typicalMetal = this.parseNumericValue(value);
              break;
          }
        }
        break;
    }

    // Update SharedService after each change using the shared computation service.
    // VM is NOT persisted automatically; only when Save is pressed.
    this.miService.persistViewData(
      this.blendNumber,
      { vmInfo: this.vmInfo, baseOilInfo: this.baseOilInfo, additives: this.additives },
      { persistVm: false },
      this.vmData
    );
  }

  // Helper method to parse numeric values
  private parseNumericValue(value: any): any {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? value : parsed;
  }

  /**
   * Formats a numeric value to 2 decimals.
   * Returns 'NaN' if the value is not valid.
   */
  private formatDecimal(value: any, decimals: number = 2): string {
    if (value === null || value === undefined || value === '' || value === 'NaN') {
      return 'NaN';
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'NaN';
    }
    return num.toFixed(decimals);
  }

  toNaNIfEmpty(value: any): string {
    if (value === null || value === undefined || value === '' || value === 'NaN') {
      return 'NaN';
    }
    return String(value);
  }

  isNaNDisplay(value: any): boolean {
    return value === null || value === undefined || value === '' || value === 'NaN';
  }

  /**
   * Format the value of a FormControl on blur.
   * @param group FormGroup containing the control
   * @param fieldName Name of the field to format
   * @param decimals Number of decimals (default: 2)
   */
  onBlurFormat(group: FormGroup, fieldName: string, decimals: number = 2): void {
    const control = group.get(fieldName);
    if (control) {
      const formatted = this.formatDecimal(control.value, decimals);
      control.setValue(formatted, { emitEvent: false });
    }
  }

  trackByRowId(index: number, item: any): number {
    return item.id;
  }

}
